import React, { useCallback } from 'react';
import { Button, ContentLoader, Pagination, SkeletonLoader, Table, TableBody, TableHeader } from '@forge/common';
import {
  IActivityItem,
  IActivityPagination,
  IActivitySort,
  IColumnConfig,
  SortBy,
  SortOrder,
} from '../../Redux/activityModels';
import ActivityTableRow from './ActivityTableRow';
import '../activity.scss';
import { useTranslation } from 'react-i18next';
import { SORT_DIRECTION, SortDirection, buildHeaderData, getPaginationInfoLabel } from '../../utils';

export interface ActivityTableProps {
  items: IActivityItem[];
  sort: IActivitySort;
  pagination: IActivityPagination;
  isLoading: boolean;
  isInitialLoad: boolean;
  columnConfig: IColumnConfig;
  hasActiveFilters: boolean;
  onSort: (sortBy: SortBy, sortOrder: SortOrder) => void;
  onRowClick: (item: IActivityItem) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onResetFilters: () => void;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const ActivityTable: React.FC<ActivityTableProps> = ({
  items,
  sort: { sortBy, sortOrder },
  pagination: { currentPage, totalItems, totalPages, pageSize },
  isLoading,
  isInitialLoad,
  columnConfig,
  hasActiveFilters,
  onSort,
  onRowClick,
  onPageChange,
  onPageSizeChange,
  onResetFilters,
}) => {
  const handleSort = useCallback(
    (direction: SortDirection, id: string) => {
      const sortOrder = direction === SORT_DIRECTION.Desc ? SortOrder.Desc : SortOrder.Asc;
      onSort(id as SortBy, sortOrder);
    },
    [onSort],
  );

  const { order, visibility } = columnConfig;
  const visibleColumns = order.filter(id => visibility[id]);
  const { t } = useTranslation();
  const headerData = buildHeaderData(sortBy, sortOrder, t, visibleColumns);

  if (isLoading && isInitialLoad) {
    return (
      <div className="tw-flex tw-flex-col activity-table-container tw-flex-1" data-testid="activity-table-loader">
        <div className="tw-flex tw-flex-1 tw-justify-center tw-items-center">
          <ContentLoader>
            {t('activityLogLoading')}
          </ContentLoader>
        </div>
      </div>
    );
  }

  const skeletonRows = isLoading
    ? Array.from({ length: 10 }, (_, i) => (
        <tr key={`skeleton-${i}`} data-testid="activity-skeleton-row">
          {visibleColumns.map(colId => (
            <td key={colId} className="tw-py-3 tw-px-4">
              <SkeletonLoader width={100} />
            </td>
          ))}
        </tr>
      ))
    : null;

  // When active filters produce zero results (e.g. after an auto-refresh removes
  // a previously selected status), inform the user and offer a one-click escape.
  // The generic "No data found" text is shown for search-only or truly empty results.
  const emptyStateContent = hasActiveFilters ? (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
      <p className="tw-text-sm activity-text-secondary tw-m-0">{t('activityLogFiltersChanged')}</p>
      <Button variant="secondary" onClick={onResetFilters} data-testid="activity-reset-filters-btn">
        {t('activityLogResetFilters')}
      </Button>
    </div>
  ) : (
    <span className="tw-text-xl activity-text-secondary">{t('noDataFound')}</span>
  );

  const emptyState = (
    <tr>
      <td colSpan={visibleColumns.length} className="tw-text-center tw-py-12" data-testid="activity-empty-state">
        {emptyStateContent}
      </td>
    </tr>
  );

  const dataRows = items.map(item => (
    <ActivityTableRow key={item.id} item={item} visibleColumns={visibleColumns} onRowClick={onRowClick} />
  ));

  const tableBodyContent = skeletonRows ?? (items.length === 0 ? emptyState : dataRows);

  return (
    <div className="tw-flex tw-flex-col activity-table-container">
      <div className="activity-table-scroll tw-overflow-x-auto tw-flex-1">
        <Table data-testid="activity-table">
          <TableHeader data={headerData} onSort={handleSort} canResize />
          <TableBody>
            {tableBodyContent}
          </TableBody>
        </Table>
      </div>
      {items.length > 0 && (
        <div className="tw-px-4 tw-py-2 tw-mt-auto">
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            totalPages={totalPages}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            showPageInfo
            pageInfoLabel={getPaginationInfoLabel(currentPage, pageSize, totalItems, t)}
            showPageGoTo
            pageGoToLabel={t('activityLogPageGoTo')}
            showPageSizeList
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeListLabel={t('activityLogRowPerPage')}
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(ActivityTable);
